import { useEffect, useState } from "react";
import { imgExt, extOf, getFileIcon } from "../utils/fileTypes";
import { formatBytes, formatDate } from "../utils/format";
import { fetchBlobUrl } from "../api";
import { getCachedUrl, setCachedUrl, hasCachedUrl } from "../utils/blobCache";

function FolderGlyph() {
  return <span className="file-icon">📂</span>;
}

function Thumbnail({ filePath, token, name }) {
  const [url, setUrl] = useState(() => getCachedUrl(filePath) || null);

  useEffect(() => {
    if (url || hasCachedUrl(filePath)) return;
    let cancelled = false;
    fetchBlobUrl(token, filePath)
      .then((u) => {
        if (cancelled) return;
        setCachedUrl(filePath, u);
        setUrl(u);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [filePath, token, url]);

  if (!url) return <div className="thumb-placeholder">{getFileIcon(name)}</div>;
  return <img className="thumb-img" src={url} alt={name} loading="lazy" />;
}

export default function FileItem({
  item,
  index,
  filePath,
  token,
  viewMode,
  selected,
  focused,
  isRenaming,
  renameValue,
  onRenameChange,
  onRenameCommit,
  onRenameCancel,
  onToggleSelect,
  onClick,
  onOpen,
  onContextMenu,
  draggable,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  isDropTarget,
}) {
  const isImage = !item.isDirectory && imgExt.includes(extOf(item.name));

  const rowClass = [
    viewMode === "grid" ? "grid-item" : "list-item",
    selected ? "selected" : "",
    focused ? "focused" : "",
    isDropTarget ? "drop-target" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const nameEl = isRenaming ? (
    <input
      className="rename-input"
      value={renameValue}
      autoFocus
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => onRenameChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") onRenameCommit();
        if (e.key === "Escape") onRenameCancel();
        e.stopPropagation();
      }}
      onBlur={onRenameCommit}
    />
  ) : (
    <span className="file-name" title={item.name}>
      {item.name}
    </span>
  );

  return (
    <div
      className={rowClass}
      data-index={index}
      onClick={onClick}
      onDoubleClick={onOpen}
      onContextMenu={onContextMenu}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      role="button"
      tabIndex={-1}
    >
      <input
        type="checkbox"
        className="item-checkbox"
        checked={!!selected}
        onChange={() => {}}
        onClick={(e) => {
          e.stopPropagation();
          onToggleSelect(e);
        }}
      />

      {viewMode === "grid" ? (
        <>
          <div className="grid-thumb">
            {item.isDirectory ? (
              <span className="thumb-folder">📂</span>
            ) : isImage ? (
              <Thumbnail filePath={filePath} token={token} name={item.name} />
            ) : (
              <span className="thumb-placeholder">{getFileIcon(item.name)}</span>
            )}
          </div>
          {nameEl}
        </>
      ) : (
        <>
          {item.isDirectory ? <FolderGlyph /> : <span className="file-icon">{getFileIcon(item.name)}</span>}
          {nameEl}
          <span className="col-size">{item.isDirectory ? "" : formatBytes(item.size)}</span>
          <span className="col-modified">{formatDate(item.modifiedAt)}</span>
        </>
      )}
    </div>
  );
}
